import React, { useEffect, useMemo, useState } from "react";
import { Modal, Skeleton, Wizard, useWizard } from "@picklebet/ui";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { useWithdrawalSettings } from "@/hooks/firestore/user/useWithdrawalSettings";
import {
  WithdrawWizardContext,
  type WithdrawWizardContextState,
} from "../../WithdrawWizardContext";
import type { WithdrawCurrencyDescriptor } from "../../types";
import {
  findWithdrawCurrency,
  type WithdrawStepName,
} from "../../utils/registry";
import { useBlockingCardVerification } from "../../hooks/useBlockingCardVerification";
import { useWithdrawInitialStep } from "../../hooks/useWithdrawInitialStep";
import { useWithdrawWizard } from "../../hooks/useWithdrawWizard";
import { WithdrawSuccess } from "./blocks/withdraw-success/WithdrawSuccess";
import * as styles from "./WithdrawWizard.module.scss";

const WizardHeader = ({ backAction = true }: { backAction?: boolean }) => {
  const { submitting } = useWithdrawWizard();

  return (
    <Wizard.Header backAction={backAction} backDisabled={submitting}>
      <Modal.CloseAction disabled={submitting} />
    </Wizard.Header>
  );
};

/**
 * `Wizard.Root` reads `initialStep` once, but the verification record is
 * written server-side moments after the deposit that creates the requirement.
 * A user who taps Withdraw straight after depositing on a card that skipped
 * 3DS would otherwise reach bank details with the card still unvalidated
 * (PKB-13612). The records are live, so send them to validation the moment one
 * lands. Only AUD registers the step, and a completed withdrawal is left
 * alone.
 */
const CardVerificationGate = () => {
  const { activeStep, goTo } = useWizard<WithdrawStepName>();
  const { verification } = useBlockingCardVerification();

  const mustRedirect =
    Boolean(verification) &&
    activeStep !== "withdraw/card-verification" &&
    activeStep !== "withdraw-success";

  useEffect(() => {
    if (!mustRedirect) return;

    goTo("withdraw/card-verification", { replace: true });
  }, [mustRedirect, goTo]);

  return null;
};

type SettledWithdrawWizardProps = {
  descriptor: WithdrawCurrencyDescriptor;
  withdrawableAmountInCents: number;
  initialStep: WithdrawStepName;
  close: () => void;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
  mustVerifyToWithdraw: boolean;
  showVerificationRequired: () => void;
  canWithdraw: boolean;
  showWithdrawUnavailable: () => void;
  canManageWallets: boolean;
};

const SettledWithdrawWizard = ({
  descriptor,
  withdrawableAmountInCents,
  initialStep,
  close,
  submitting,
  setSubmitting,
  mustVerifyToWithdraw,
  showVerificationRequired,
  canWithdraw,
  showWithdrawUnavailable,
  canManageWallets,
}: SettledWithdrawWizardProps) => {
  const [amount, setAmount] = useState(0);
  const [skrillEmail, setSkrillEmail] = useState<string>();
  const [interacWalletId, setInteracWalletId] = useState<string>();

  const wizardState = useMemo(
    () =>
      ({
        amount,
        setAmount,
        currency: descriptor.id,
        withdrawableAmountInCents,
        skrillEmail,
        setSkrillEmail,
        interacWalletId,
        setInteracWalletId,
        submitting,
        setSubmitting,
        mustVerifyToWithdraw,
        showVerificationRequired,
        canWithdraw,
        showWithdrawUnavailable,
        canManageWallets,
        close,
      }) satisfies WithdrawWizardContextState,
    [
      amount,
      descriptor.id,
      withdrawableAmountInCents,
      skrillEmail,
      interacWalletId,
      submitting,
      setSubmitting,
      mustVerifyToWithdraw,
      showVerificationRequired,
      canWithdraw,
      showWithdrawUnavailable,
      canManageWallets,
      close,
    ],
  );

  return (
    <WithdrawWizardContext value={wizardState}>
      <Wizard.Root initialStep={initialStep}>
        {descriptor.id === "AUD" && <CardVerificationGate />}

        <Wizard.Step name="withdraw" title={descriptor.primary.title}>
          <WizardHeader />
          <descriptor.primary.Component />
        </Wizard.Step>

        {(descriptor.steps ?? []).map(
          ({ name, title, Component, terminal }) => (
            <Wizard.Step key={name} name={name} title={title}>
              <WizardHeader backAction={!terminal} />
              <Component />
            </Wizard.Step>
          ),
        )}

        <Wizard.Step name="withdraw-success">
          <WizardHeader backAction={false} />
          <WithdrawSuccess />
        </Wizard.Step>
      </Wizard.Root>
    </WithdrawWizardContext>
  );
};

/**
 * Stands in for every phase that resolves before the wizard can mount. Sized to
 * the settled view so the modal doesn't jump when the data lands.
 */
export const WithdrawWizardSkeleton = () => {
  const currency = useFiatCurrency();
  const descriptor = findWithdrawCurrency(currency);

  if (!descriptor) {
    return null;
  }

  return (
    <div className={styles.loadingStep}>
      <Modal.Header>
        <Modal.Heading>
          <Skeleton size="xs" className={styles.titleSkeleton} />
        </Modal.Heading>
      </Modal.Header>

      <descriptor.primary.Skeleton />
    </div>
  );
};

export const WithdrawWizard = ({
  close,
  submitting,
  setSubmitting,
  mustVerifyToWithdraw,
  showVerificationRequired,
  canWithdraw,
  showWithdrawUnavailable,
  canManageWallets,
}: {
  close: () => void;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
  mustVerifyToWithdraw: boolean;
  showVerificationRequired: () => void;
  canWithdraw: boolean;
  showWithdrawUnavailable: () => void;
  canManageWallets: boolean;
}) => {
  const currency = useFiatCurrency();
  const { settings, isLoading: settingsLoading } =
    useWithdrawalSettings(currency);

  // only the account's own currency mounts, so its steps are the only ones
  // registered and step names can't collide across currencies
  const descriptor = findWithdrawCurrency(currency);

  const { initialStep, isSettled: initialStepSettled } = useWithdrawInitialStep(
    descriptor?.id ?? "AUD",
  );

  if (!descriptor) {
    return (
      <p className={styles.unsupportedCurrency}>
        Withdrawals are not available for your account currency. Please contact
        support.
      </p>
    );
  }

  // wait until settled so Wizard.Root mounts with the right step
  if (settingsLoading || !initialStepSettled) {
    return <WithdrawWizardSkeleton />;
  }

  return (
    <SettledWithdrawWizard
      descriptor={descriptor}
      withdrawableAmountInCents={settings.withdrawableAmount}
      initialStep={initialStep}
      close={close}
      submitting={submitting}
      setSubmitting={setSubmitting}
      mustVerifyToWithdraw={mustVerifyToWithdraw}
      showVerificationRequired={showVerificationRequired}
      canWithdraw={canWithdraw}
      showWithdrawUnavailable={showWithdrawUnavailable}
      canManageWallets={canManageWallets}
    />
  );
};
